import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, ArrowUpRight, ImageOff } from 'lucide-react';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

import { formatTND } from '../../lib/currency';
import { addToCart } from '../../lib/cart';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { useScrollContainer } from '../../context/ScrollContainerContext';
import { getActivePromo, getEffectivePrice, getPromoFlashColor, getPromoToneClasses, sortItemsByPromo } from '../../lib/promo';
import { shouldShowPrices } from '../../lib/priceVisibility';

const PENDING_CART_KEY = 'etc_pending_cart_add';

function getVisiblePageNumbers(currentPage, totalPages) {
  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function buildSparePartCardImageAlt(sparePart, language) {
  const name = String(sparePart?.name || '').trim();
  const category = String(sparePart?.category || '').trim();

  if (language === 'fr') {
    return [name, category ? `photo de ${category.toLowerCase()}` : 'photo de piece de rechange']
      .filter(Boolean)
      .join(' - ');
  }

  return [name, category ? `${category} spare part photo` : 'spare part photo']
    .filter(Boolean)
    .join(' - ');
}

export default function SparePartsGridSection({
  spareParts,
  isLoading,
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
}) {
  const { t, language } = useI18n();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const scrollContainerRef = useScrollContainer();
  const gridRef = useRef(null);
  const lastScrolledPageRef = useRef(currentPage);
  const orderedSpareParts = sortItemsByPromo(spareParts);
  const visiblePages = getVisiblePageNumbers(currentPage, totalPages);

  // When the user clicks a pagination control, scroll the grid back to the
  // top of the visible viewport. Without this the user can end up staring at
  // the footer (especially on mobile) if they were already scrolled near
  // the bottom of the previous page. We anchor on the grid container (not
  // the wrapping <section>) so the first card sits cleanly below the sticky
  // header regardless of any decorative padding above the grid. The Layout
  // uses a custom scroll container, so we have to scroll that element
  // rather than the window. We skip the very first render to avoid fighting
  // the natural initial scroll position (e.g. deep links / browser-restored
  // scroll).
  useEffect(() => {
    if (lastScrolledPageRef.current === currentPage) return;
    lastScrolledPageRef.current = currentPage;

    const container = scrollContainerRef?.current;
    const target = gridRef.current;
    if (!target) return;

    // Measure the sticky header so we leave breathing room between it and
    // the first card. Fall back to a sensible default when the header
    // cannot be measured (e.g. tests, SSR).
    const stickyHeader = document.querySelector('header')?.closest('.sticky');
    const headerHeight = stickyHeader?.getBoundingClientRect().height ?? 80;
    const extraOffset = 16;

    if (container) {
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const offsetWithinContainer = targetTop - containerTop;
      const nextTop = container.scrollTop + offsetWithinContainer - headerHeight - extraOffset;
      container.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
    } else if (typeof window !== 'undefined') {
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [currentPage, scrollContainerRef]);

  function shouldIgnoreCardClick(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('a,button,input,select,textarea,label,[role="button"]'));
  }

  function handleCardNavigation(event, itemId) {
    if (!itemId || shouldIgnoreCardClick(event.target)) return;
    router.push(`/spare-parts/${itemId}`);
  }

  function handleCardKeyDown(event, itemId) {
    if (!itemId || shouldIgnoreCardClick(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      router.push(`/spare-parts/${itemId}`);
    }
  }

  function handleAddToCart(item) {
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        const payload = { ...item, quantity: 1, kind: 'spare-part' };
        sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify(payload));
      }
      router.push(`/auth/login?next=${encodeURIComponent(router.asPath || '/spare-parts')}`);
      return;
    }
    addToCart(item, 1, { kind: 'spare-part' });
    toast.success(`${item.name} ${t('sparePartsPage.grid.addedToCart')}`);
  }

  return (
    <section className="pb-12">
      <div className="container mx-auto px-6 lg:px-10">
        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="animate-pulse rounded-2xl border border-white/10 bg-[var(--dash-black)] p-4">
                <div className="h-44 rounded-xl bg-[#111]" />
                <div className="mt-4 h-4 w-2/3 rounded bg-white/10" />
                <div className="mt-2 h-3 w-5/6 rounded bg-white/10" />
                <div className="mt-4 h-3 w-1/3 rounded bg-white/10" />
              </div>
            ))}
          </div>
        ) : (
          <div ref={gridRef} className="space-y-12">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {spareParts.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-white/15 bg-black/30 p-10 text-center text-white">
                  <p className="text-lg font-semibold">{t('sparePartsPage.grid.noMatchesTitle')}</p>
                  <p className="mt-2 text-sm text-white/70">{t('sparePartsPage.grid.noMatchesText')}</p>
                </div>
              )}

              {orderedSpareParts.map((item) => {
                const activePromo = getActivePromo(item, language);
                const promoTone = getPromoToneClasses(activePromo?.colorTone);
                const basePrice = Number(item?.price ?? 0);
                const effectivePrice = getEffectivePrice(item, language);
                const hasDiscount = activePromo?.kind === 'price' && effectivePrice < basePrice;
                const showPrices = shouldShowPrices();

                return (
                <article
                  key={item._id}
                  role="link"
                  tabIndex={0}
                  onClick={(event) => handleCardNavigation(event, item._id)}
                  onKeyDown={(event) => handleCardKeyDown(event, item._id)}
                  className="group isolate flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-[var(--dash-black)] transition-all duration-300 hover:border-[var(--dash-main)]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-main)]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dash-black)]"
                >
                <div className="relative z-10 aspect-[4/3] overflow-hidden bg-[#fff]">
                  {item.images && item.images.length > 0 ? (
                    <div className="relative h-full w-full">
                      <ImageOnlySkeletonLoader
                        image={item.images[0]}
                        mode="native"
                        alt={buildSparePartCardImageAlt(item, language)}
                        containerClassName="relative h-full w-full"
                        imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        skeletonEnabled
                        skeletonRadius="none"
                      />
                    </div>
                  ) : (

                    <div className="relative flex h-full flex-col items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,rgba(var(--dash-main-rgb),0.12),transparent_42%),linear-gradient(180deg,rgba(15,15,20,0.04),rgba(15,15,20,0.02))] px-5 text-center">
                      <div className="relative flex h-14 w-14 items-center justify-center rounded-[20px] border border-black/10 bg-white/80 text-[var(--dash-main)] shadow-[0_20px_50px_-30px_rgba(var(--dash-main-rgb),0.45)]">
                        <ImageOff className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.26em] text-zinc-700">
                        {t('common.noImage')}
                      </p>
                      <p className="mt-2 max-w-[12rem] text-xs uppercase tracking-[0.16em] text-zinc-500">
                        {t('productDetails.visualComingSoon')}
                      </p>
                    </div>
                  )}
                  <span className="absolute left-3 top-3 z-30 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90">
                    {item.category || t('common.general')}
                  </span>
                  {activePromo ? (
                    <span
                      className={`promo-badge-flash absolute right-3 top-3 z-30 overflow-visible rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${promoTone.badge}`}
                      style={{ '--promo-flash-color': getPromoFlashColor(activePromo?.colorTone) }}
                    >
                      {activePromo.label || activePromo.title || t('common.promo', 'Promo')}
                    </span>
                  ) : null}
                </div>

                <div className="relative z-10 flex flex-1 flex-col p-5">
                  <h3 className="line-clamp-2 min-h-[3rem] text-base font-semibold text-white">{item.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-white/70">{item.description}</p>
                  {activePromo?.title || activePromo?.description ? (
                    <div className={`mt-3 rounded-xl border px-3 py-2 text-xs ${promoTone.callout}`}>
                      <p className="font-semibold uppercase tracking-[0.14em]">
                        {activePromo.title || activePromo.label || t('common.promo', 'Promo')}
                      </p>
                      {activePromo.description ? (
                        <p className="mt-1 text-white/80">{activePromo.description}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-auto pt-4">
                    {showPrices ? (
                      hasDiscount ? (
                        <div className="space-y-1">
                          <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-white/45 line-through">
                            {formatTND(basePrice)}
                          </span>
                          <span className="block text-base font-bold text-[var(--dash-other)]">
                            {formatTND(effectivePrice)}
                          </span>
                        </div>
                      ) : (
                        <span className="block text-base font-bold text-[var(--dash-other)]">
                          {formatTND(basePrice)}
                        </span>
                      )
                    ) : (
                      <span className="block text-base font-bold text-[var(--dash-other)]">
                        {t('common.priceOnRequest', 'Price on request')}
                      </span>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleAddToCart(item)}
                      className="inline-flex items-center rounded-lg bg-[var(--dash-main)] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-white transition hover:bg-[#ff6b28]"
                    >
                      {t('sparePartsPage.grid.addToCart')}
                    </button>
                    <Link
                      href={`/spare-parts/${item._id}`}
                      className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/82 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5 text-[var(--dash-main)]" />
                      {t('sparePartsPage.grid.viewDetails')}
                    </Link>
                    </div>
                  </div>
                </div>
                </article>
                );
              })}
            </div>

            {totalPages > 1 ? (
              <div className="flex flex-col items-center justify-center gap-4 pt-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/75 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('common.previous', 'Previous')}
                </button>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  {visiblePages.map((page, index) => {
                    const prevPage = visiblePages[index - 1];
                    const showGap = index > 0 && page - prevPage > 1;
                    return (
                      <div key={`page-slot-${page}`} className="flex items-center gap-2">
                        {showGap ? <span className="text-sm text-white/35">...</span> : null}
                        <button
                          type="button"
                          onClick={() => onPageChange(page)}
                          aria-current={currentPage === page ? 'page' : undefined}
                          className={`inline-flex h-10 min-w-10 items-center justify-center rounded-xl border px-3 text-sm font-semibold transition-all duration-300 ${
                            currentPage === page
                              ? 'border-[var(--dash-main)] bg-[var(--dash-main)] text-white'
                              : 'border-white/15 text-white/70 hover:border-[var(--dash-main)] hover:text-white'
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/75 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                >
                  {t('common.next', 'Next')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
